// src/components/BlinkingPlaceholderTextarea.tsx

import React, { useState, useRef, useEffect, useId } from 'react';

interface BlinkingPlaceholderTextareaProps {
  placeholder: string;
  animatedPlaceholders?: readonly string[];
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyPress: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  rows?: number;
  className?: string;
  maxLength?: number;
  readOnly?: boolean;
}

const BlinkingPlaceholderTextarea = React.forwardRef<HTMLTextAreaElement, BlinkingPlaceholderTextareaProps>((
  {
    placeholder,
    animatedPlaceholders,
    value,
    onChange,
    onKeyPress,
    rows = 1,
    className = '',
    maxLength,
    readOnly
  },
  ref
) => {
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // FIX: Generate a unique ID for each instance to avoid collisions.
  const textareaId = useId();

  const showPlaceholder = !isFocused && !value;

  const [animatedPlaceholder, setAnimatedPlaceholder] = useState('');
  useEffect(() => {
    if (!animatedPlaceholders?.length || value.trim().length > 0) {
      setAnimatedPlaceholder('');
      return;
    }

    let timeoutId: number | undefined;
    let phraseIndex = 0;
    let charIndex = 0;
    let isDeleting = false;

    const tick = () => {
      const full = animatedPlaceholders[phraseIndex] ?? '';
      if (!isDeleting) {
        charIndex += 1;
        setAnimatedPlaceholder(full.slice(0, charIndex));
        if (charIndex >= full.length) {
          timeoutId = window.setTimeout(() => {
            isDeleting = true;
            timeoutId = window.setTimeout(tick, 55);
          }, 2500);
          return;
        }
        timeoutId = window.setTimeout(tick, 40);
        return;
      }

      charIndex -= 1;
      setAnimatedPlaceholder(full.slice(0, Math.max(0, charIndex)));
      if (charIndex <= 0) {
        isDeleting = false;
        phraseIndex = (phraseIndex + 1) % animatedPlaceholders.length;
        timeoutId = window.setTimeout(tick, 450);
        return;
      }
      timeoutId = window.setTimeout(tick, 22);
    };

    timeoutId = window.setTimeout(tick, 300);
    return () => {
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [animatedPlaceholders, value]);

  const effectivePlaceholder = animatedPlaceholders?.length ? animatedPlaceholder : placeholder;

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => setIsFocused(false);

  // Enhanced auto-resize textarea height with mobile optimization
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const newHeight = Math.min(textarea.scrollHeight, 120); // Max height of 120px
      textarea.style.height = `${newHeight}px`;
      textarea.style.overflowY = textarea.scrollHeight > 120 ? 'auto' : 'hidden';
      
      // Add haptic feedback on mobile when expanding
      if (textarea.scrollHeight > 60 && navigator.vibrate) {
        navigator.vibrate(5);
      }
    }
  }, [value]);

  return (
    <div className="placeholder-container">
      <label
        // FIX: Conditionally apply `htmlFor` for better screen reader behavior.
        htmlFor={showPlaceholder ? textareaId : undefined}
        className={`placeholder-label ${!showPlaceholder ? 'hidden' : ''}`}
      >
        {effectivePlaceholder}
        <span className="blinking-cursor">_</span>
      </label>

      <textarea
        id={textareaId}
        ref={ref || textareaRef}
        // FIX: Add aria-label for accessibility since the visual label disappears.
        aria-label={effectivePlaceholder}
        value={value}
        onChange={onChange}
        onKeyPress={onKeyPress}
        onFocus={handleFocus}
        onBlur={handleBlur}
        rows={rows}
        className={`${className} textarea-transparent-bg`}
        maxLength={maxLength}
        readOnly={readOnly}
        placeholder="" // Real placeholder is intentionally empty
      />
    </div>
  );
});

export default BlinkingPlaceholderTextarea;
